// React Compiler opt-out: the resend button's enabled state and the displayed address are read
// off the mandark cache during render (isResourcePopulated()/getResource()), which the useFluxStore
// bridge doesn't cover — compiled caching would leave the button permanently disabled. The session
// reads here ARE bridged; remove this once the mandark reads are too.
'use no memo';

import React, {useEffect, useMemo, useRef, useState} from 'react';

import {addToast, Banner, Button, Dialogue, Heading, Icon, Modal, Text} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {refreshSession} from '@albert-io/mandark/authentication';

import appStore from 'client/AppStore';
import {useFluxStore} from 'client/framework';
import {hardRedirectToLoginPage} from 'client/LogIn/utils';
import sessionStore from 'client/Session/SessionStore';
import {getBaseUserQuery} from 'client/User/UserQueryUtil';
import {redirect} from 'client/User/UserRedirectUtil';
import {genericCancelableMandarkRequest} from 'resources/mandark.resource';

import './email-verification-notice.scss';

const CANNOT_CONFIRM_ACTIVE_USER = 'Errors.BadRequest.ValidationError.CannotConfirmActiveUser';
const INVALID_CONFIRMATION_LINK = 'Errors.BadRequest.ValidationError.InvalidConfirmationLink';
const GENERIC_ERROR = 'We were unable to process your verification request.';
const EXPIRED_LINK_ERROR = 'The confirmation link you have provided is no longer valid.';

/** The two deep-link params this view reads off the router. */
interface ConfirmEmailLinkParams {
  emailLinkId?: string;
  userId?: string;
}

/** The subset of a mandark error response this view branches on. */
interface MandarkErrorLike {
  message?: string;
  response?: {body?: {errors?: {code?: string; title?: string}[]}};
}

/** The one mutation the resend path performs on the resolved `user_v2` model. */
interface ResendableUser {
  setSentConfirmationEmailAt(sentAt: null): {save(): Promise<unknown>};
}

/** `.done()` hands back an untyped config bag; the request helper needs the path narrowed. */
interface ConfirmAccountQuery {
  resourcePath: string[];
  customQuery?: Record<string, unknown>;
}

/**
 * `appStore.routerProps` is an opaque bag (`Record<string, unknown>`), so the deep link's
 * query is narrowed here rather than at each read.
 */
const getLinkParams = (): ConfirmEmailLinkParams => {
  const {location} = appStore.routerProps as {location?: {query?: ConfirmEmailLinkParams}};
  return location?.query ?? {};
};

/**
 * The confirm email component will confirm an email for a user that is logged in, logged out, or
 * even logged in as a different user than the one they are trying to confirm.
 *
 * The confirm email deep link that takes users to this view contains the following query parameters
 * that are used by this view:
 *
 * @example
 * {
 *   userId: '1234', // id of the user we will confirm
 *   emailLinkId: '4321' // special token used by the BE to ensure we are who we say we are
 * }
 *
 * The incoming query parameters BOTH have to be present for this view to make a request; they both
 * make up the payload we're sending.
 *
 * Only a logged in user will see the option to RESEND their confirmation email.
 */
const ConfirmEmail = () => {
  const [error, setError] = useState<string | null>(null);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const isMountedRef = useRef(false);
  const cancelRef = useRef<(() => void) | null>(null);

  const hasValidSession = useFluxStore(sessionStore, (s) => s.hasValidSession());
  const userId = useFluxStore(sessionStore, (s) => s.getUserId());

  // Built only while signed in: the class only ever reached this query from inside its
  // `hasValidSession()` render branch, so building it for an absent user would register a
  // mandark endpoint the class never requested.
  const activeUserQuery = useMemo(
    () => (hasValidSession ? getBaseUserQuery(userId) : null),
    [hasValidSession, userId]
  );

  useEffect(() => {
    isMountedRef.current = true;

    const processEmailConfirmation = async () => {
      if (sessionStore.hasValidSession() && sessionStore.isUserConfirmed()) {
        await redirect();
        return;
      }

      const {emailLinkId, userId: linkUserId} = getLinkParams();

      // Since this component can short-circuit rendering via logic in `App.react.js` we
      // bail if we're in a state where we are unable to confirm our email.
      if (!emailLinkId && !linkUserId) {
        return;
      }

      try {
        const query = resource('users_v2')
          .mandarkEndpoint(['users_v2', linkUserId, 'confirm_account'])
          .customQuery({verify_confirmation_link: emailLinkId})
          .done() as unknown as ConfirmAccountQuery;

        const {promise, cancel} = genericCancelableMandarkRequest('patch', query);
        cancelRef.current = cancel;
        await promise;

        if (isMountedRef.current) {
          setIsConfirmed(true);
        }
      } catch (err) {
        const mandarkError = err as MandarkErrorLike;
        let nextError = GENERIC_ERROR;
        const firstError = mandarkError.response?.body?.errors?.[0];

        // if user is already confirmed, quietly suppress error and show confirmation modal
        if (
          firstError?.code === CANNOT_CONFIRM_ACTIVE_USER &&
          firstError?.title === 'Cannot Confirm Active User'
        ) {
          if (isMountedRef.current) {
            setIsConfirmed(true);
            return;
          }
        } else if (mandarkError.message?.includes(INVALID_CONFIRMATION_LINK)) {
          /**
           * Since we're catching a `genericMandarkRequest` the thrown error isn't
           * fully parsed. We'll introspect the message for a status code we know how
           * to handle... otherwise set a generic error message.
           */
          nextError = EXPIRED_LINK_ERROR;
        }

        if (isMountedRef.current) {
          setError(nextError);
        }
      }
    };

    processEmailConfirmation();

    return () => {
      isMountedRef.current = false;
      if (cancelRef.current) {
        cancelRef.current();
        cancelRef.current = null;
      }
    };
  }, []);

  const handleModalClose = () => {
    // Read at fire time, not from the render snapshot.
    if (sessionStore.hasValidSession()) {
      refreshSession().then(() => {
        redirect();
      });
    } else {
      hardRedirectToLoginPage();
    }
  };

  const resendConfirmationEmail = async () => {
    const user = (await getBaseUserQuery(
      sessionStore.getUserId()
    ).getResourcePromise()) as ResendableUser;
    try {
      await user.setSentConfirmationEmailAt(null).save();
      addToast({
        title: 'Email sent.',
        color: 'positive',
        message: "A confirmation email has been sent to the email you've provided."
      });
    } catch {
      addToast({
        title: 'An error was encountered.',
        color: 'negative',
        message: 'We were unable to resend a confirmation email at this time, please try again.'
      });
    }
  };

  /*
    When logged in, we render the re-send button, we want the user to be able to re-send their email
    at any time, not necessarily only if their request failed.

    The actual CONFIRMATION is only happening when the user mounts this view: when they enter their
    email link in the browser and come to this page, or when they click the email link and get to
    this page.
  */
  return (
    <div className='email-verification-notice'>
      {isConfirmed ? (
        <Modal ariaLabel='Your account has been verified.' handleClose={handleModalClose}>
          {({CloseButtonWrapper, modalContentStyle}) => (
            // No `handleClose` on Dialogue: `hideCloseBtn` means it renders no close button to
            // fire one, so the Modal's own `handleClose` is what actually closes this.
            <Dialogue
              inModal
              hideCloseBtn
              alignTitle='center'
              title={<Icon icon='check-circle' iconStyle='regular' />}
              className={`email-verification-modal ${modalContentStyle}`}
            >
              <Dialogue.Body align='center'>
                {hasValidSession ? (
                  <Text>Your email has been confirmed. Welcome to Albert!</Text>
                ) : (
                  <Text>
                    Your email has been confirmed. Welcome to Albert! In order to continue,
                    you&apos;ll need to log in.
                  </Text>
                )}
              </Dialogue.Body>
              <Dialogue.BtnGroup fillColumn direction='column'>
                <CloseButtonWrapper>
                  <Button>{hasValidSession ? 'Ok' : 'Log in'}</Button>
                </CloseButtonWrapper>
              </Dialogue.BtnGroup>
            </Dialogue>
          )}
        </Modal>
      ) : null}
      {error ? (
        <Banner type='negative' className='mb-x1'>
          <Banner.Icon icon='exclamation-triangle' iconStyle='regular' />
          <Text color='primary'>
            <b>We were unable to verify your account.</b>
            <br />
            {error}
          </Text>
        </Banner>
      ) : null}
      {hasValidSession && activeUserQuery ? (
        <div className='rounded-x1 border border-solid border-border-primary background-primary p-x4 text-center'>
          <Heading as='h2'>Please Verify Your Email</Heading>
          <Text>
            <p>
              We&apos;ve sent a confirmation email to{' '}
              <b>{activeUserQuery.getResource().getEmail()}</b>. Please confirm your email address
              by clicking the confirmation link in the email.
            </p>
            <p>
              If you can&apos;t find the confirmation email in your inbox, please try one of the
              following:
            </p>
            <ol className='email-verification-notice__instructions mt-x4'>
              <li className='mb-x1'>
                Check your spam folder and double-check your inbox for a message titled
                &quot;Please verify your email for Albert.io&quot;
              </li>
              <li className='mb-x1'>
                Contact your school administrator or IT administrator and ensure they are not
                blocking emails coming from the albert.io domain.
              </li>
              <li className='mb-x1'>
                Click the button below to have a new confirmation email sent to your email address.
              </li>
            </ol>
            <Button
              disabled={!activeUserQuery.isResourcePopulated()}
              small
              onClick={resendConfirmationEmail}
              className='mt-x4'
            >
              Resend confirmation email
            </Button>
          </Text>
        </div>
      ) : null}
    </div>
  );
};

export default ConfirmEmail;
